import { ref, computed, watch } from 'vue';

export function useProgramFilters(props, currentTab) {
    const selectedProgramTypes = ref([]);
    const selectedAddedToMyPoints = ref([]);
    const singleUserToggle = ref(false);
    const selectedBonusPrograms = ref([]);
    const bonusProgramSearchQuery = ref('');

    const programTypeOptions = [
        { id: 'airline', name: 'Airline' },
        { id: 'hotel', name: 'Hotel' },
        { id: 'rail', name: 'Rail' },
        { id: 'flexible_currency', name: 'Flexible Currency' },
    ];

    const userProgramsData = computed(() => props.userPrograms || []);

    const isSingleUser = computed(() => addedToMyPointsOptions.value.length <= 1);

    // Members come from userPrograms (the My Points graph), not program.user_points,
    // which only holds manual balances for this one program (SUPPORT-830).
    const addedToMyPointsOptions = computed(() =>
        userProgramsData.value.map((userProgram) => ({
            id: userProgram.user_id.toString(),
            name: userProgram.user_name,
        }))
    );

    const bonusProgramOptions = computed(() => {
        if (!props.program?.transfer_partners) return [];

        let partners = props.program.transfer_partners;
        if (selectedProgramTypes.value.length > 0) {
            partners = partners.filter((p) => selectedProgramTypes.value.includes(p.destination_program_type));
        }
        if (currentTab.value === 'transfer-bonus-history') {
            partners = partners.filter((p) => Array.isArray(p.historical_bonuses) && p.historical_bonuses.length > 0);
        }

        const uniquePrograms = [];
        const seen = new Set();
        partners.forEach((partner) => {
            if (partner.destination_program_name && partner.destination_program_id) {
                const key = `${partner.destination_program_id}-${partner.destination_program_name}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    uniquePrograms.push({ id: partner.destination_program_id, name: partner.destination_program_name });
                }
            }
        });
        return uniquePrograms.sort((a, b) => a.name.localeCompare(b.name));
    });

    const filteredBonusProgramOptions = computed(() => {
        if (!bonusProgramSearchQuery.value.trim()) return bonusProgramOptions.value;
        const term = bonusProgramSearchQuery.value.toLowerCase();
        return bonusProgramOptions.value.filter((p) => p.name.toLowerCase().includes(term));
    });

    const isAnyFilterApplied = computed(() =>
        selectedProgramTypes.value.length > 0 ||
        selectedAddedToMyPoints.value.length > 0 ||
        selectedBonusPrograms.value.length > 0 ||
        singleUserToggle.value === true
    );

    const activeFiltersCount = computed(() => {
        if (currentTab.value === 'transfer-bonus-history') return selectedProgramTypes.value.length + selectedBonusPrograms.value.length;
        const types = selectedProgramTypes.value.length;
        const points = isSingleUser.value ? (singleUserToggle.value ? 1 : 0) : selectedAddedToMyPoints.value.length;
        return types + points;
    });

    const filteredTransferPartners = computed(() => {
        if (!props.program.transfer_partners) return [];
        let filtered = props.program.transfer_partners;

        if (selectedProgramTypes.value.length > 0) {
            filtered = filtered.filter((p) => selectedProgramTypes.value.includes(p.destination_program_type));
        }

        if (currentTab.value === 'current-rates') {
            if (isSingleUser.value && singleUserToggle.value) {
                // No My Points programs means nothing matches, not "skip the filter" (SUPPORT-830).
                const userProgramList = userProgramsData.value[0]?.programs || [];
                filtered = filtered.filter((p) => userProgramList.some((prog) => prog.id === p.destination_program_id));
            } else if (!isSingleUser.value && selectedAddedToMyPoints.value.length > 0) {
                const selectedUserIds = selectedAddedToMyPoints.value.map((u) => parseInt(u.id));
                filtered = filtered.filter((p) => selectedUserIds.some((uid) => {
                    const up = userProgramsData.value.find((u) => u.user_id === uid);
                    return up?.programs.some((prog) => prog.id === p.destination_program_id);
                }));
            }
        }

        if (currentTab.value === 'transfer-bonus-history' && selectedBonusPrograms.value.length > 0) {
            filtered = filtered.filter((p) => selectedBonusPrograms.value.includes(p.destination_program_id));
        }

        return filtered;
    });

    const historyRows = computed(() => {
        if (!props.program.transfer_partners) return [];
        let partners = props.program.transfer_partners;

        if (selectedProgramTypes.value.length > 0) {
            partners = partners.filter((p) => selectedProgramTypes.value.includes(p.destination_program_type));
        }
        partners = partners.filter((p) => Array.isArray(p.historical_bonuses) && p.historical_bonuses.length > 0);

        if (selectedBonusPrograms.value.length > 0) {
            partners = partners.filter((p) => selectedBonusPrograms.value.includes(p.destination_program_id));
        }

        const rows = [];
        partners.forEach((p) => {
            p.historical_bonuses.forEach((b) => {
                rows.push({
                    id: p.id,
                    bonus_id: b.id,
                    destination_program_id: p.destination_program_id,
                    destination_program_name: p.destination_program_name,
                    destination_program_type: p.destination_program_type,
                    destination_program_image: p.destination_program_image,
                    transfer_ratio: p.transfer_ratio,
                    bonus_ratio: b.bonus_ratio,
                    bonus_multiplier: b.bonus_multiplier,
                    bonus_percentage: b.bonus_percentage,
                    effective_transfer_ratio: b.effective_transfer_ratio,
                    base_transfer_ratio: b.base_transfer_ratio,
                    bonus_end_date: b.bonus_end_date,
                    status: b.status,
                });
            });
        });

        const parseDate = (s) => {
            if (!s || typeof s !== 'string') return null;
            const parts = s.substring(0, 10).split('-').map((n) => parseInt(n, 10));
            return new Date(parts[0], parts[1] - 1, parts[2]);
        };

        rows.sort((a, b) => {
            const sa = a.status === 'active' ? 1 : 0;
            const sb = b.status === 'active' ? 1 : 0;
            if (sa !== sb) return sb - sa;
            const da = a.bonus_end_date ? parseDate(a.bonus_end_date)?.getTime() || 0 : 0;
            const db = b.bonus_end_date ? parseDate(b.bonus_end_date)?.getTime() || 0 : 0;
            return db - da;
        });

        return rows;
    });

    const tableRows = computed(() => {
        return currentTab.value === 'transfer-bonus-history' ? historyRows.value : filteredTransferPartners.value;
    });

    // Toggle/clear methods
    const toggleProgramType = (typeId) => {
        const idx = selectedProgramTypes.value.indexOf(typeId);
        idx > -1 ? selectedProgramTypes.value.splice(idx, 1) : selectedProgramTypes.value.push(typeId);
    };

    const clearProgramTypeFilters = () => { selectedProgramTypes.value = []; };

    const toggleAddedToMyPointsSelection = (option) => {
        const idx = selectedAddedToMyPoints.value.findIndex((p) => p.id === option.id);
        idx > -1 ? selectedAddedToMyPoints.value.splice(idx, 1) : selectedAddedToMyPoints.value.push(option);
    };

    const isAddedToMyPointsSelected = (option) => selectedAddedToMyPoints.value.some((p) => p.id === option.id);

    const toggleBonusProgram = (programId) => {
        const idx = selectedBonusPrograms.value.indexOf(programId);
        idx > -1 ? selectedBonusPrograms.value.splice(idx, 1) : selectedBonusPrograms.value.push(programId);
    };

    const isBonusProgramSelected = (programId) => selectedBonusPrograms.value.includes(programId);

    const clearBonusProgramFilters = () => { selectedBonusPrograms.value = []; bonusProgramSearchQuery.value = ''; };

    const clearCurrentRatesFilters = () => {
        selectedProgramTypes.value = [];
        isSingleUser.value ? (singleUserToggle.value = false) : (selectedAddedToMyPoints.value = []);
    };

    const clearAddedToMyPointsFilter = () => { selectedAddedToMyPoints.value = []; };

    const clearAllFilters = () => {
        selectedProgramTypes.value = [];
        selectedAddedToMyPoints.value = [];
        selectedBonusPrograms.value = [];
        bonusProgramSearchQuery.value = '';
        singleUserToggle.value = false;
    };

    return {
        selectedProgramTypes,
        selectedAddedToMyPoints,
        singleUserToggle,
        selectedBonusPrograms,
        bonusProgramSearchQuery,
        programTypeOptions,
        isSingleUser,
        addedToMyPointsOptions,
        bonusProgramOptions,
        filteredBonusProgramOptions,
        isAnyFilterApplied,
        activeFiltersCount,
        filteredTransferPartners,
        historyRows,
        tableRows,
        toggleProgramType,
        clearProgramTypeFilters,
        toggleAddedToMyPointsSelection,
        isAddedToMyPointsSelected,
        toggleBonusProgram,
        isBonusProgramSelected,
        clearBonusProgramFilters,
        clearCurrentRatesFilters,
        clearAddedToMyPointsFilter,
        clearAllFilters,
    };
}
